import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Body1, Caption1, Spinner, MessageBar, MessageBarBody, mergeClasses,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useValueAtRisk } from '../../../api/hooks';
import type { ValueAtRiskCell } from '../../../api/types';

// FEAT-018 Value-at-Risk heat-map. 3×3 grid (Business-Value bucket × Risk score). The
// top-right cell (High value × Risk 1) is emphasised — mitigation budget should
// concentrate there.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  grid: {
    display: 'grid',
    gridTemplateColumns: '4rem repeat(3, 1fr)',
    gridTemplateRows: '2rem repeat(3, minmax(6rem, 1fr))',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'stretch',
  },
  cornerLabel: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  axisLabel: { display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.colorNeutralForeground2 },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cellHot: {
    // High-value high-risk — emphasised so leadership eyes land here first.
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
  cellWarm: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
  },
  cellEmphasis: {
    // Heavy shadow lifts the priority cell off the grid without needing a border-width
    // token that isn't present in every Fluent v9 minor release.
    boxShadow: tokens.shadow16,
  },
  cellCount: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold },
});

const VALUES: Array<'Low' | 'Med' | 'High'> = ['Low', 'Med', 'High'];
const RISKS: Array<1 | 2 | 3> = [1, 2, 3];

export function ValueAtRiskPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useValueAtRisk(projectId);

  function find(value: 'Low' | 'Med' | 'High', risk: 1 | 2 | 3): ValueAtRiskCell | undefined {
    return data?.cells.find((c) => c.valueBucket === value && c.riskBucket === risk);
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Value at Risk</Title2>
        <Body1 className={styles.hint}>
          Where mitigation budget should land. High-value × Risk 1 (top right) is the priority cell.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project to see its value/risk distribution.</Body1>
      ) : isLoading ? (
        <Spinner label="Bucketing work items…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : (
        <div className={styles.grid}>
          <div className={styles.cornerLabel}><Caption1>Risk ↓ / BV →</Caption1></div>
          {VALUES.map((v) => <div key={v} className={styles.axisLabel}><Caption1>{v}</Caption1></div>)}
          {RISKS.map((r) => (
            <>
              <div key={`risk-${r}`} className={styles.axisLabel}><Caption1>{r === 1 ? '1-High' : r === 2 ? '2-Med' : '3-Low'}</Caption1></div>
              {VALUES.map((v) => {
                const cell = find(v, r);
                const isHot = v === 'High' && r === 1;
                const isWarm = (v === 'High' && r === 2) || (v === 'Med' && r === 1);
                return (
                  <div
                    key={`${v}-${r}`}
                    className={mergeClasses(
                      styles.cell,
                      isHot && styles.cellHot,
                      isWarm && styles.cellWarm,
                      isHot && styles.cellEmphasis,
                    )}
                    aria-label={`Value ${v} Risk ${r}: ${cell?.count ?? 0} items, ${cell?.totalStoryPoints ?? 0} story points`}
                  >
                    <span className={styles.cellCount}>{cell?.count ?? 0}</span>
                    <Caption1>{(cell?.totalStoryPoints ?? 0).toFixed(0)} SP</Caption1>
                  </div>
                );
              })}
            </>
          ))}
        </div>
      )}
    </div>
  );
}
